import { GridColDef, GridRenderEditCellParams, useGridApiContext } from '@mui/x-data-grid-pro';
import { useCallback, useMemo } from 'react';
import { Checkbox } from '@mui/material';
import { GridApiPro } from '@mui/x-data-grid-pro/models/gridApiPro';

import { dataGridCol, gridBooleanCell, gridCurrencyCell, gridZipCell } from '@shared/components/grid/common';
import { GridColNumber } from '@shared/components/grid/GridColNumber';
import { GridColSelect } from '@shared/components/grid/GridColSelect';
import { GridColTextInput } from '@shared/components/grid/GridColTextInput';
import { IMap } from '@shared/interfaces';
import { ADDRESS_TYPE_OPTIONS } from '@shared/lists';
import { statesList } from '@shared/constants/statesList';

import { NContact } from '../../validation';
import { ContactSection } from '../ContactSection';
import { contactAddressValidationSchema } from './validation';

interface IAddressProps {
	data?: NContact.Address[];
  mutateCombinedDti?: () => void;
	onChange(data: NContact.Address[]): Promise<IMap<string> | undefined>;
}

export const Address = ({ data = [], mutateCombinedDti, onChange }: IAddressProps) => {
  
  const handleChange = useCallback((checked: boolean, params: GridRenderEditCellParams, api: React.MutableRefObject<GridApiPro>) => {
    if (params.field === 'isCurrent') {
      api.current.setEditCellValue({ id: params.id, field: 'isCurrent', value: checked });
      checked && api.current.setEditCellValue({ id: params.id, field: 'includeInDti', value: checked });
    } else {
      api.current.setEditCellValue({ id: params.id, field: 'includeInDti', value: checked });
    }
  }, []);

  const rows = useMemo(
    () =>
      data
        .map((row) => ({
          ...row,
          yrs: Math.floor((row.monthsAtAddress || 0) / 12),
          mths: Math.floor((row.monthsAtAddress || 0) % 12),
        }))
        .sort((a, b) => Number(b.isCurrent) - Number(a.isCurrent)),
    [data]
  );

  const columns: GridColDef<NContact.Address>[] = useMemo(
    () =>
      [
        dataGridCol('isCurrent', {
          headerName: 'Current',
          minWidth: 55,
          flex: 0.4,
          ...gridBooleanCell,
          renderEditCell: (params) => {
            const api = useGridApiContext();

            return (
              <Checkbox
                checked={params.value}
                onChange={(_, checked) => handleChange(checked, params, api)}
              />
            );
          }
        }),
        dataGridCol('includeInDti', {
          headerName: 'DTI',
          minWidth: 50,
          flex: 0.4,
          ...gridBooleanCell,
          renderEditCell: (params) => {
            const api = useGridApiContext();

            return (
              <Checkbox
                checked={params.value}
                disabled={!params.row.isCurrent}
                onChange={(_, checked) => handleChange(checked, params, api)}
              />
            );
          }
        }),
        dataGridCol('streetLine1', {
          headerName: 'Street',
          minWidth: 150,
          flex: 3,
          textWrap: true,
          renderEditCell: (params) => <GridColTextInput params={params} />,
        }),
        dataGridCol('unit', {
          headerName: 'Unit #',
          minWidth: 100,
          flex: 1.5,
          textWrap: true,
          renderEditCell: (params) => <GridColTextInput params={params} />,
        }),
        dataGridCol('city', {
          minWidth: 100,
          flex: 1,
          textWrap: true,
          renderEditCell: (params) => <GridColTextInput params={params} />,
        }),
        dataGridCol('state', {
          editable: true,
          minWidth: 50,
          flex: 0.7,
          renderEditCell: (params) => (
            <GridColSelect
              isSearchable
              canBeClean={false}
              params={params}
              options={statesList}
            />
          ),
        }),
        dataGridCol('county', {
          minWidth: 100,
          flex: 1.5,
          renderEditCell: (params) => (
            <GridColTextInput
              disabled
              params={params}
            />
          ),
        }),
        dataGridCol('zip', {
          ...gridZipCell,
          minWidth: 70,
          flex: 1.1,
          textWrap: true,
        }),
        dataGridCol('type', {
          minWidth: 100,
          flex: 1,
          renderEditCell: (params) => (
            <GridColSelect
              isSearchable
              options={ADDRESS_TYPE_OPTIONS}
              params={params}
            />
          ),
        }),
        dataGridCol('yrs', {
          minWidth: 40,
          flex: 0.5,
          renderEditCell: (params) => (
            <GridColNumber
              params={params}
              limit={2}
            />
          ),
        }),
        dataGridCol('mths', {
          minWidth: 40,
          flex: 0.6,
          renderEditCell: (params) => (
            <GridColNumber
              params={params}
              limit={2}
            />
          ),
        }),
        dataGridCol('monthsAtAddress', {
          hide: true,
          valueSetter: ({ row }) => ({
            ...row,
            monthsAtAddress: (+row.yrs || 0) * 12 + (+row.mths || 0),
          }),
        }),
        dataGridCol('monthlyPayment', {
          headerName: 'Payment',
          minWidth: 80,
          flex: 1,
          textWrap: true,
          ...gridCurrencyCell,
          renderEditCell: (params) => (
            <GridColNumber
              params={params}
              decimalScale={2}
              prefix="$"
            />
          ),
        }),
      ].map((i) => ({ ...i, editable: true, sortable: false, disableColumnMenu: true })),
    []
  );

  return (
    <ContactSection
      strict
      disableSelectionOnClick
      name="Address"
      buttonTitle="+ New"
      data={rows}
      columns={columns}
      validateSchema={contactAddressValidationSchema}
      mutateCombinedDti={mutateCombinedDti}
      onChange={onChange}
    />
  );
};
